import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, InputNumber, Select, message } from 'antd';
import { VehicleTypeDetail } from '../../../../graphql/types/VehicleType';
import ImageUploader from 'components/ImageUploader';
import { FuelTypeEnum, getFuelTypeLabel } from '../../../../enums/FuelTypeEnum';
import { useMutation } from '@apollo/client';
import { CREATE_VEHICLE, UPDATE_VEHICLE } from '../graphql/mutations/vehicleMutations';
import EditableDrawer from 'components/EditableDrawer';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { useTranslation } from 'react-i18next';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';

interface VehicleFormDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: VehicleTypeDetail) => void;
  initialValues?: Partial<VehicleTypeDetail>;
  mode: 'create' | 'edit';
  modelOptions: { label: string; value: number; status: number }[];
  statusOptions: { label: string; value: number }[];
}

const inputStyle = { width: 240 };

const VehicleFormDrawer: React.FC<VehicleFormDrawerProps> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
  mode,
  modelOptions,
  statusOptions,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentWarehouse: warehouse } = useWarehouseContext();
  const { t } = useTranslation();
  const { vehicleTypes } = useVehicleTypes();

  const filteredModelOptions = useMemo(() => {
    return modelOptions.filter(option => option.status === 1);
  }, [modelOptions]);

  const [createVehicle] = useMutation(CREATE_VEHICLE);
  const [updateVehicle] = useMutation(UPDATE_VEHICLE);

  // FuelType下拉
  const fuelTypeOptions = Object.values(FuelTypeEnum)
      .filter(v => typeof v === 'number')
      .map(v => ({ label: getFuelTypeLabel(v as FuelTypeEnum, t), value: v as number }));

  // 处理车型选中后的自动填充
  const handleTypeChange = (vehicleTypeId: number) => {
    const matched = vehicleTypes?.find(vt => String(vt.id) === String(vehicleTypeId));
    if (matched) {
      form.setFieldsValue({
        maxWeightLbs: matched.defaultMaxWeightLbs,
        maxVolumeFt3: matched.defaultMaxVolumeFt3,
        lengthFt: matched.defaultLengthFt,
        widthFt: matched.defaultWidthFt,
        heightFt: matched.defaultHeightFt,
      });
    }
  };

  // 打开抽屉时重置表单并加载所有HUB
  useEffect(() => {
    if (visible) {
      form.resetFields();

      if (initialValues) {
        const typeOption = modelOptions.find(opt => String(opt.value) === String(initialValues.vehicleTypeId));
        const isTypeSuspended = typeOption?.status === 0;
        form.setFieldsValue({
          ...initialValues,
          vehicleTypeId: (initialValues.vehicleTypeId && !isTypeSuspended) ? String(initialValues.vehicleTypeId) : undefined
        });
      } else {
        form.setFieldsValue({
          warehouseId: warehouse?.id,
        });
      }
    }
  }, [visible, initialValues, form, warehouse, modelOptions]);

  // 提交表单
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      
      if (mode === 'create') {
        await createVehicle({ variables: { input: values } });
        message.success(t('pickup.vehicle.messages.createSuccess') as string);
      } else if (mode === 'edit' && initialValues?.id) {
        values.id = initialValues.id;
        await updateVehicle({ variables: { input: values } });
        message.success(t('pickup.vehicle.messages.updateSuccess') as string);
      }
      
      // 调用父组件的onSubmit函数，触发列表重新查询
      onSubmit(values);
      onClose();
    } catch (error: any) {
      if (error.errorFields) {
        console.error('Form validation failed:', error);
      } else {
        console.error('Submit failed:', error);
        const gqlErr = error?.graphQLErrors?.[0];
        const messageKey: string | undefined = gqlErr?.extensions?.messageKey;
        message.error(messageKey ? t(messageKey) as string : error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const drawerTitle = mode === 'create' ? t('pickup.vehicle.pageDetails.new') : t('pickup.vehicle.pageDetails.update');

  return (
    <EditableDrawer
      title={drawerTitle}
      visible={visible}
      onClose={onClose}
      onSubmit={handleSubmit}
      confirmText={isSubmitting ? t('pickup.vehicle.pageDetails.processing') as string : t('pickup.vehicle.pageDetails.confirm') as string}
      cancelText={t('pickup.vehicle.pageDetails.cancel') as string}
      confirmDisabled={isSubmitting}
      width={550}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        initialValues={initialValues}
      >
        <Form.Item name="vehicleNumber" label={t('pickup.vehicle.pageDetails.id') as string} hidden={mode !== 'edit'}>
          <Input disabled style={inputStyle} />
        </Form.Item>
        <Form.Item 
            name="licensePlate" 
            label={t('pickup.vehicle.pageDetails.licensePlate') as string} 
            rules={[{ required: true, message: t('pickup.vehicle.messages.pleaseInputLicensePlate') as string }]}
        >
            <Input placeholder={t('pickup.vehicle.placeholders.inputLicensePlate') as string} style={inputStyle} />
        </Form.Item>
        <Form.Item name="fuelType" label={t('pickup.vehicle.pageDetails.fuelType') as string} rules={[{ required: true, message: t('pickup.vehicle.messages.pleaseSelectFuelType') as string }]}> 
            <Select options={fuelTypeOptions} placeholder={t('pickup.vehicle.placeholders.selectFuelType') as string} style={inputStyle} />
        </Form.Item>
        <Form.Item name="vehicleTypeId" label={t('pickup.vehicle.pageDetails.model') as string} rules={[{ required: true, message: t('pickup.vehicle.messages.pleaseSelectModel') as string }]}>
            <Select
                options={filteredModelOptions}
                placeholder={t('pickup.vehicle.placeholders.selectModel') as string}
                style={inputStyle}
                onChange={handleTypeChange}
            />
        </Form.Item>
        <Form.Item name="maxWeightLbs" label={t('pickup.vehicle.pageDetails.maxWeightLbs') as string} rules={[{ required: true, type: "number", message: t('pickup.vehicle.messages.pleaseInputPayload') as string }]}> 
            <InputNumber min={0.01} max={99999999.99} precision={2} style={inputStyle} placeholder={t('pickup.vehicle.placeholders.inputPayload') as string} />
        </Form.Item>
        <Form.Item name="maxVolumeFt3" label={t('pickup.vehicle.pageDetails.maxVolumeFt3') as string} rules={[{ required: true, type: "number", message: t('pickup.vehicle.messages.pleaseInputCapacity') as string }]}> 
            <InputNumber min={0.01} max={9999999.999} precision={3} style={inputStyle} placeholder={t('pickup.vehicle.placeholders.inputCapacity') as string} />
        </Form.Item>
        <Form.Item name="lengthFt" label={t('pickup.vehicle.pageDetails.lengthFt') as string} rules={[{ required: true, type: "number", message: t('pickup.vehicle.messages.pleaseInputLength') as string }]}> 
            <InputNumber min={0.01} max={999999.99} precision={2} style={inputStyle} placeholder={t('pickup.vehicle.placeholders.inputLength') as string} />
        </Form.Item>
        <Form.Item name="widthFt" label={t('pickup.vehicle.pageDetails.widthFt') as string} rules={[{ required: true, type: "number", message: t('pickup.vehicle.messages.pleaseInputWidth') as string }]}> 
            <InputNumber min={0.01} max={999999.99} precision={2} style={inputStyle} placeholder={t('pickup.vehicle.placeholders.inputWidth') as string} />
        </Form.Item>
        <Form.Item name="heightFt" label={t('pickup.vehicle.pageDetails.heightFt') as string} rules={[{ required: true, type: "number", message: t('pickup.vehicle.messages.pleaseInputHeight') as string }]}> 
            <InputNumber min={0.01} max={999999.99} precision={2} style={inputStyle} placeholder={t('pickup.vehicle.placeholders.inputHeight') as string} />
        </Form.Item>
        <Form.Item name="warehouseId" label={t('pickup.vehicle.pageDetails.warehouseName') as string} rules={[{ required: true, message: t('pickup.vehicle.messages.pleaseSelectHub') as string }]}> 
            <Select
                options={warehouse ? [{ label: warehouse.name, value: warehouse.id }] : []}
                disabled={true}
                style={inputStyle}
            />
        </Form.Item>
        <Form.Item name="status" label={t('pickup.vehicle.pageDetails.status') as string}> 
            <Select options={statusOptions} placeholder={t('pickup.vehicle.placeholders.select') as string} style={inputStyle} defaultValue={1} /> 
        </Form.Item>
        <Form.Item name="vehiclePhotos" label={t('pickup.vehicle.pageDetails.vehiclePhoto') as string}>
          <ImageUploader
              value={initialValues?.vehiclePhotos || []}
              displayUrls={initialValues?.vehiclePhotoUrls || []}
              onChange={(urls: string[]) => form.setFieldsValue({vehiclePhotos: urls})}
              maxCount={3}
          />
        </Form.Item>
      </Form>
    </EditableDrawer>
  );
};

export default VehicleFormDrawer;
